import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Descriptions, Divider, Flex, Space, Tag, Typography } from 'antd';
import type { DescriptionsProps } from 'antd';
import { useEffect, useState } from 'react';
import { Config, ConfigItem } from '../api/go';

interface ProcessDataTaskConfigData {
  report_filter: number;
  total_report: number;
  register_day: number;
  keyword_list: string[];
}

interface ProcessDataTaskV2ConfigData {
  report_filter: number;
  total_report: number;
  register_day: number;
  credit_log_reason: string;
}

interface MapReportScoreConfig {
  exempt_games: number;
  score_per_game: number;
  duration_filter?: number;
  count_filter?: number;
}

interface MapReportTaskConfig {
  afk: MapReportScoreConfig;
  dead: MapReportScoreConfig;
  report_types: string[];
  credit_log_reason: string;
}

interface HighRiskNewAccountTaskConfigData {
  report_range: number;
  report_filter: number;
  total_report: number;
  register_day: number;
  ban_count: number;
}

interface GrayConfigDescriptor {
  key: string;
  title: string;
  usage: string;
  parse: (raw: string) => unknown;
  render: (parsed: unknown) => React.ReactNode;
}

interface GrayConfigCardState {
  item?: ConfigItem;
  parsed?: unknown;
  error?: string;
  parseError?: string;
}

const threeColumnLayout = { xs: 1, sm: 2, md: 3 };

function getErrorMessage(err: any) {
  if (err?.status) {
    return `请求失败: ${err.status}`;
  }
  if (err?.message) {
    return err.message;
  }
  return '请求失败';
}

function renderStatusTag(status?: number) {
  if (status === 1) {
    return <Tag color="success">启用</Tag>;
  }
  if (status === 0) {
    return <Tag color="error">禁用</Tag>;
  }
  return <Tag>未知</Tag>;
}

function renderTagList(values?: string[], emptyText = '未配置') {
  if (!values || values.length === 0) {
    return <Typography.Text type="secondary">{emptyText}</Typography.Text>;
  }

  return (
    <Flex gap="small" wrap>
      {values.map((value) => (
        <Tag key={value}>{value}</Tag>
      ))}
    </Flex>
  );
}

function renderLineList(values?: string[], emptyText = '未配置') {
  if (!values || values.length === 0) {
    return <Typography.Text type="secondary">{emptyText}</Typography.Text>;
  }

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      <textarea readOnly style={{ width: '100%', height: 500, resize: 'none' }} value={JSON.stringify(values, null, 2)} />
    </Space>
  );
}

function renderColorTagList(values?: string[], emptyText = '未配置') {
  if (!values || values.length === 0) {
    return <Typography.Text type="secondary">{emptyText}</Typography.Text>;
  }

  const colors = ['magenta', 'volcano', 'orange', 'gold', 'lime', 'cyan', 'blue', 'geekblue'];

  return (
    <Flex gap="small" wrap>
      {values.map((value, index) => (
        <Tag key={value} color={colors[index % colors.length]}>
          {value}
        </Tag>
      ))}
    </Flex>
  );
}

function renderConfigSectionTitle(title: string) {
  return (
    <Typography.Text strong style={{ fontSize: 14 }}>
      {title}
    </Typography.Text>
  );
}

const ConfigTaskProcessData: GrayConfigDescriptor = {
  key: 'process_data_task_config',
  title: '进程关键词扫描配置',
  usage: '用于 server/scheduler/task_process_data.go 中的 CheckProcessData 定时任务。',
  parse: (raw: string) => JSON.parse(raw) as ProcessDataTaskConfigData,
  render: (parsed: unknown) => {
    const config = parsed as ProcessDataTaskConfigData;

    const items: DescriptionsProps['items'] = [
      {
        key: 'report_filter',
        label: '单小时举报人数',
        children: config.report_filter,
      },
      {
        key: 'total_report',
        label: '历史举报总数',
        children: config.total_report,
      },
      {
        key: 'register_day',
        label: '注册天数限制',
        children: config.register_day,
      },
    ];

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {renderConfigSectionTitle('任务参数')}
        <Descriptions bordered column={threeColumnLayout} size="small" items={items} />
        {renderConfigSectionTitle('关键词列表')}
        {renderLineList(config.keyword_list, '当前没有关键词')}
      </Space>
    );
  },
};

const ConfigTaskProcessDataV2: GrayConfigDescriptor = {
  key: 'config:process_data_task_v2',
  title: '进程异常检测 V2 配置',
  usage: '用于 server/scheduler/task_process_data_v2.go 中的 CheckProcessDataV2 定时任务。',
  parse: (raw: string) => JSON.parse(raw) as ProcessDataTaskV2ConfigData,
  render: (parsed: unknown) => {
    const config = parsed as ProcessDataTaskV2ConfigData;

    const items: DescriptionsProps['items'] = [
      {
        key: 'report_filter',
        label: '单小时举报人数',
        children: config.report_filter,
      },
      {
        key: 'total_report',
        label: '历史举报总数',
        children: config.total_report,
      },
      {
        key: 'register_day',
        label: '注册天数限制',
        children: config.register_day,
      },
    ];

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {renderConfigSectionTitle('任务参数')}
        <Descriptions bordered column={threeColumnLayout} size="small" items={items} />
        {renderConfigSectionTitle('信誉分展示文案')}
        <Card size="small">{config.credit_log_reason || <Typography.Text type="secondary">未配置</Typography.Text>}</Card>
      </Space>
    );
  },
};

const TaskMapReport: GrayConfigDescriptor = {
  key: 'config:map_report_task',
  title: '地图上报扣分配置',
  usage: '用于 server/scheduler/task_map_report.go 中的 CheckMapReportData 定时任务。',
  parse: (raw: string) => JSON.parse(raw) as MapReportTaskConfig,
  render: (parsed: unknown) => {
    const config = parsed as MapReportTaskConfig;

    const afkItems: DescriptionsProps['items'] = [
      {
        key: 'afk_exempt_games',
        label: '每日免处罚局数',
        children: config.afk?.exempt_games ?? '-',
      },
      {
        key: 'afk_duration_filter',
        label: '挂机时长阈值',
        children: config.afk?.duration_filter ?? '-',
      },
      {
        key: 'afk_score_per_game',
        label: '每局扣分',
        children: config.afk?.score_per_game ?? '-',
      },
    ];

    const deadItems: DescriptionsProps['items'] = [
      {
        key: 'dead_exempt_games',
        label: '每日免处罚局数',
        children: config.dead?.exempt_games ?? '-',
      },
      {
        key: 'dead_count_filter',
        label: '死亡次数阈值',
        children: config.dead?.count_filter ?? '-',
      },
      {
        key: 'dead_score_per_game',
        label: '每局扣分',
        children: config.dead?.score_per_game ?? '-',
      },
    ];

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {renderConfigSectionTitle('举报类型')}
        <Card size="small">{renderColorTagList(config.report_types, '当前没有举报类型')}</Card>
        {renderConfigSectionTitle('信誉分展示文案')}
        <Card size="small">{config.credit_log_reason || <Typography.Text type="secondary">未配置</Typography.Text>}</Card>
        <Divider style={{ margin: '8px 0' }} />
        {renderConfigSectionTitle('AFK 处罚规则')}
        <Descriptions bordered column={threeColumnLayout} size="small" items={afkItems} />
        {renderConfigSectionTitle('Dead 处罚规则')}
        <Descriptions bordered column={threeColumnLayout} size="small" items={deadItems} />
      </Space>
    );
  },
};

const TaskHighRiskNewAccount: GrayConfigDescriptor = {
  key: 'config:high_risk_new_account_task',
  title: '高风险新账号扫描配置',
  usage: '用于 server/scheduler/task_high_risk_new_account.go 中的 CheckHighRiskNewAccount 定时任务。',
  parse: (raw: string) => JSON.parse(raw) as HighRiskNewAccountTaskConfigData,
  render: (parsed: unknown) => {
    const config = parsed as HighRiskNewAccountTaskConfigData;

    const items: DescriptionsProps['items'] = [
      {
        key: 'report_range',
        label: '举报时间范围（小时）',
        children: config.report_range,
      },
      {
        key: 'report_filter',
        label: '举报人数阈值',
        children: config.report_filter,
      },
      {
        key: 'total_report',
        label: '历史举报总数',
        children: config.total_report,
      },
      {
        key: 'register_day',
        label: '注册天数限制',
        children: config.register_day,
      },
      {
        key: 'ban_count',
        label: 'UUID 上被封号数量阈值',
        children: config.ban_count,
      },
    ];

    return (
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {renderConfigSectionTitle('任务参数')}
        <Descriptions bordered column={threeColumnLayout} size="small" items={items} />
      </Space>
    );
  },
};

const configDescriptors: GrayConfigDescriptor[] = [
  TaskMapReport,
  TaskHighRiskNewAccount,
  ConfigTaskProcessData,
  ConfigTaskProcessDataV2,
];

function GrayConfigCard(props: { descriptor: GrayConfigDescriptor; state?: GrayConfigCardState; loading: boolean }) {
  const { descriptor, state, loading } = props;
  if (!state) {
    return (
      <Card title={descriptor.title} loading={loading} styles={{ header: { backgroundColor: '#fafafa' } }}>
        <Typography.Text type="secondary">正在加载配置...</Typography.Text>
      </Card>
    );
  }

  const item = state?.item;

  const baseInfoItems: DescriptionsProps['items'] = [
    {
      key: 'name',
      label: '名称',
      children: item?.name || <Typography.Text type="secondary">接口未返回</Typography.Text>,
    },
    {
      key: 'key',
      label: 'Key',
      children: item?.key || descriptor.key,
    },
    {
      key: 'status',
      label: '状态',
      children: renderStatusTag(item?.status),
    },
  ];

  const extraInfoItems: DescriptionsProps['items'] = [
    {
      key: 'rule',
      label: '规则',
      children: item?.rule || <Typography.Text type="secondary">未配置</Typography.Text>,
    },
    {
      key: 'usage',
      label: '使用位置',
      children: descriptor.usage,
    },
  ];

  return (
    <Card
      title={descriptor.title}
      extra={renderStatusTag(item?.status)}
      loading={loading && !state}
      styles={{ header: { backgroundColor: '#fafafa' } }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Descriptions bordered column={threeColumnLayout} size="small" items={baseInfoItems} />
        <Descriptions bordered column={1} size="small" items={extraInfoItems} />

        {state?.error ? <Alert showIcon type="error" message="配置加载失败" description={state.error} /> : null}

        {!state?.error && item && !item.key ? (
          <Alert showIcon type="warning" message="未找到配置" description={`接口未返回 key 为 ${descriptor.key} 的配置。`} />
        ) : null}

        {!state?.error && state?.parseError ? <Alert showIcon type="error" message="配置解析失败" description={state.parseError} /> : null}

        {!state?.error && item?.key && !state?.parseError && state.parsed !== undefined
          ? state.parsed && descriptor.render(state.parsed)
          : null}
      </Space>
    </Card>
  );
}

export default function GrayConfigPage() {
  const [loading, setLoading] = useState(false);
  const [configMap, setConfigMap] = useState<Record<string, GrayConfigCardState>>({});

  async function loadConfigs() {
    setLoading(true);

    try {
      const entries = await Promise.all(
        configDescriptors.map(async (descriptor) => {
          try {
            const item = await Config.getConfig(descriptor.key);
            const nextState: GrayConfigCardState = { item };

            if (!item?.key) {
              return [descriptor.key, nextState] as const;
            }

            try {
              nextState.parsed = descriptor.parse(item.config);
            } catch (err) {
              nextState.parseError = getErrorMessage(err);
            }

            return [descriptor.key, nextState] as const;
          } catch (err) {
            return [
              descriptor.key,
              {
                error: getErrorMessage(err),
              },
            ] as const;
          }
        }),
      );

      setConfigMap(Object.fromEntries(entries));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadConfigs();
  }, []);

  const failedCount = configDescriptors.filter((descriptor) => configMap[descriptor.key]?.error).length;

  return (
    <div style={{ textAlign: 'left' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          styles={{ body: { paddingBottom: 16 } }}
          extra={
            <Button icon={<ReloadOutlined />} onClick={loadConfigs} loading={loading}>
              刷新配置
            </Button>
          }
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              GrayRuleConfig 配置查看
            </Typography.Title>
            <Typography.Text type="secondary">
              当前页面只展示代码中已实际使用的 GrayRuleConfig，并按 3 列优先的方式平铺展示。
            </Typography.Text>
            {failedCount > 0 ? (
              <Alert showIcon type="warning" message={`有 ${failedCount} 项配置加载失败，可点击右上角刷新重试。`} />
            ) : null}
          </Space>
        </Card>

        {configDescriptors.map((descriptor) => (
          <GrayConfigCard key={descriptor.key} descriptor={descriptor} state={configMap[descriptor.key]} loading={loading} />
        ))}
      </Space>
    </div>
  );
}
